import { useCallback, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import type { IssueCluster, IssueOccurrence, SignalKind } from "@backstory/protocol";
import { EndUser } from "../../../components/EndUser";
import { ErrorState, Skeleton } from "../../../components/States";
import { AutoLoadMore, PageHeader, useToast } from "../../../components/ui";
import { formatDateTime, formatDuration, formatRelative } from "../../../lib/format";
import { insightsApi, insightsKeys, type IssuePatch } from "../api";
import { BarChart, LineChart } from "../charts";
import { AssigneeSelect, Card, evidenceHref, isSnoozed, RegressionBadge, STATUSES, SeverityBadge, StatusBadge, kindLabel, replayHref, statusLabel } from "../components";
import { FrameList } from "../ErrorStack";
import { api, queryKeys } from "../../../api";
import { useAuth } from "../../../auth/store";
import { useCan } from "../../../auth/roles";
import { aiApi, aiKeys, askWhy, savedGitHubRepositories, useAiCapability, useDeepAnalysis, AiBlockedTooltip, AiOutput, AskWhyDrawer, JobStatus, Rejected, ClusterExplanationView, PrDraftView, type AiSettingsExt } from "../../ai";

/**
 * Hourly trend (48 points, oldest first) → one point per hour.
 * The server buckets with date_trunc('hour'), so the newest point is the
 * start of the current hour, not the current minute.
 */
export function trendSeries(trend: number[] | undefined, now = Date.now()): Array<{ t: number; v: number }> {
  const arr = trend ?? [];
  const hour = Math.floor(now / 3600_000) * 3600_000;
  return arr.map((v, i) => ({ t: hour - (arr.length - 1 - i) * 3600_000, v }));
}

/**
 * The sessions a stored explanation drew its evidence from. Citations are
 * "session:step" strings; rows written by an older explain path carry bare
 * step ids or `{stepId}` objects, which name no session and are skipped
 * rather than rendered as dead links. Nothing here throws on odd data, so a
 * saved row never takes the page down.
 */
export function citationSessions(citations: unknown): Array<{ sessionId: string; steps: number }> {
  if (!Array.isArray(citations)) return [];
  const bySession = new Map<string, Set<string>>();
  for (const c of citations) {
    if (typeof c !== "string") continue;
    const i = c.lastIndexOf(":");
    if (i <= 0) continue;
    const sessionId = c.slice(0, i);
    if (!bySession.has(sessionId)) bySession.set(sessionId, new Set());
    bySession.get(sessionId)!.add(c.slice(i + 1));
  }
  return Array.from(bySession, ([sessionId, steps]) => ({ sessionId, steps: steps.size }));
}

const BACKEND_KINDS: ReadonlySet<SignalKind> = new Set(["backend_exception", "backend_error_log", "metric_threshold"]);

/** Signals found in backend data. Their occurrences open a request; a replay is there only when one was linked. */
export function isBackendKind(kind: SignalKind): boolean {
  return BACKEND_KINDS.has(kind);
}

function prop(o: IssueOccurrence, key: string): string {
  const v = o.issue.props?.[key];
  return typeof v === "string" || typeof v === "number" ? String(v) : "";
}

/** What the occurrence row says happened, in the person's words. */
export function backendDetail(o: IssueOccurrence): string {
  if (o.issue.kind === "metric_threshold") {
    const value = prop(o, "value");
    const threshold = prop(o, "threshold");
    const cmp = prop(o, "comparator") === "below" ? "below" : "above";
    return value && threshold ? `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 })} (${cmp} ${Number(threshold).toLocaleString()})` : prop(o, "metric");
  }
  return prop(o, "message") || prop(o, "template") || prop(o, "route");
}

function BackendOccurrences({ samples, hasMore, loading, loadMore }: { samples: IssueOccurrence[]; hasMore: boolean; loading: boolean; loadMore: () => void }) {
  return (
    <Card title="Recent occurrences">
      <div className="stack-table ins-backend-occ">
      <table className="table" aria-label="Recent occurrences">
        <thead><tr><th>What happened</th><th>When</th><th>Times</th><th>Release</th><th>Replay</th><th></th></tr></thead>
        <tbody>
          {samples.map((o) => {
            const traceId = prop(o, "traceId");
            const times = Number(prop(o, "count")) || 1;
            return (
              <tr key={o.issue.id} className="static" data-testid="occurrence-row">
                <td>
                  <span className="cell-stack">
                    <span className="mono small" style={{ overflowWrap: "anywhere" }}>{backendDetail(o) || "—"}</span>
                    {prop(o, "service") && <small className="muted">{prop(o, "service")}</small>}
                  </span>
                </td>
                <td data-label="When" className="small muted" title={formatDateTime(new Date(o.issue.at).toISOString())}>{formatRelative(new Date(o.issue.at).toISOString())}</td>
                <td data-label="Times" className="mono small">{times.toLocaleString()}</td>
                <td data-label="Release" className="small muted">{o.release || o.issue.release || "—"}</td>
                <td data-label="Replay" className="small">{o.sessionId ? <Link {...replayHref(o.sessionId)}>Watch replay</Link> : <span className="muted">None linked</span>}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {traceId ? <Link to="/backend/traces/$traceId" params={{ traceId }} className="btn ghost">Open request</Link>
                    : o.issue.kind === "metric_threshold" ? <Link to="/backend/metrics" search={{ metric: prop(o, "metric") } as never} className="btn ghost">Open metric</Link>
                    : null}
                </td>
              </tr>
            );
          })}
          {samples.length === 0 && (<tr className="static"><td colSpan={6} className="muted" style={{ textAlign: "center", padding: 16 }}>No occurrences recorded yet.</td></tr>)}
        </tbody>
      </table>
      </div>
      <AutoLoadMore hasMore={hasMore} loading={loading} loadMore={loadMore} label="Loading more occurrences" />
    </Card>
  );
}

function audienceLabel(users: number, sessions: number): string {
  const who = users > 0 ? `${users.toLocaleString()} ${users === 1 ? "user" : "users"}` : "Anonymous";
  return `${who} · ${sessions.toLocaleString()} ${sessions === 1 ? "session" : "sessions"}`;
}

export function IssueDetailPage() {
  const { clusterId } = useParams({ strict: false }) as { clusterId: string };
  const search = useSearch({ strict: false }) as { fromCall?: string };
  const fromCall = typeof search.fromCall === "string" && search.fromCall !== "" ? search.fromCall : undefined;
  const callSearch = { ...search };
  delete callSearch.fromCall;
  // Opened from a call: Back returns to that call, with the list filters it was opened from.
  // Otherwise it returns to the issues list.
  const back = fromCall
    ? <Link to="/calls/$callId" params={{ callId: fromCall }} search={callSearch as never} className="btn ghost">← Call</Link>
    : <Link to="/issues" className="btn ghost">← Issues</Link>;
  const qc = useQueryClient();
  const toast = useToast();
  const can = useCan();
  const q = useQuery({ queryKey: insightsKeys.issue(clusterId), queryFn: () => insightsApi.issue(clusterId) });
  const occ = useInfiniteQuery({
    queryKey: insightsKeys.issueSessions(clusterId),
    queryFn: ({ pageParam }) => insightsApi.issueSessions(clusterId, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const patch = useMutation({
    mutationFn: (body: IssuePatch) => insightsApi.patchIssue(clusterId, body),
    onSuccess: () => { toast.push("success", "Issue updated"); void qc.invalidateQueries({ queryKey: insightsKeys.issue(clusterId) }); void qc.invalidateQueries({ queryKey: ["insights", "issues"] }); },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const auth = useAuth();
  const members = useQuery({ queryKey: queryKeys.members(auth.currentOrgId ?? ""), queryFn: () => api.orgs.members(auth.currentOrgId!), enabled: !!auth.currentOrgId, staleTime: 60_000 });
  const ai = useAiCapability();
  const settings = useQuery({ queryKey: aiKeys.settings(auth.currentOrgId ?? ""), queryFn: () => aiApi.settings(auth.currentOrgId!) as Promise<AiSettingsExt>, enabled: !!auth.currentOrgId, retry: false });
  const github = settings.data?.integrations?.github;
  const repos = savedGitHubRepositories(settings.data);
  const [repo, setRepo] = useState("");
  const chosenRepo = repo || repos[0] || "";
  const explain = useDeepAnalysis(useCallback(() => aiApi.explainCluster(clusterId), [clusterId]));
  const draft = useDeepAnalysis(useCallback(() => aiApi.draftPr(clusterId, chosenRepo), [clusterId, chosenRepo]));

  if (q.isPending) return <><PageHeader back={back} title="Issue" /><Skeleton lines={10} /></>;
  if (q.isError) return <><PageHeader back={back} title="Issue" /><div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div></>;
  const c = q.data;
  const samples = occ.data?.pages.flatMap((p) => p.items) ?? c.sample;
  const backend = isBackendKind(c.kind);

  return (
    <>
      <PageHeader
        back={back}
        title={c.title}
        subtitle={<><SeverityBadge severity={c.severity} /> <StatusBadge status={c.status} /> <RegressionBadge cluster={c} /> <span className="muted">{kindLabel(c.kind)}{c.route && c.route !== "/" ? ` · ${c.route}` : ""}{c.selector ? ` · ` : ""}</span>{c.selector && <span className="mono small">{c.selector}</span>}</>}
        actions={
          can.write && <div className="ins-status-row" role="group" aria-label="Status controls">
            <select aria-label="Set status" value={c.status} onChange={(e) => patch.mutate({ status: e.target.value as IssueCluster["status"] })} autoComplete="off">
              {STATUSES.map((s) => (<option key={s} value={s}>{statusLabel(s)}</option>))}
            </select>
            <AssigneeSelect value={c.assignee ?? ""} onChange={(assignee) => patch.mutate({ assignee })} disabled={patch.isPending} />
            {isSnoozed(c.snoozedUntil) ? (
              <button type="button" className="btn" onClick={() => patch.mutate({ snoozedUntil: "" })} title={`Snoozed until ${formatDateTime(c.snoozedUntil!)}`}>Unsnooze</button>
            ) : (
              <select aria-label="Snooze duration" value="" disabled={patch.isPending} autoComplete="off" onChange={(e) => {
                const hours = Number(e.target.value);
                if (!hours) return;
                patch.mutate({ snoozedUntil: new Date(Date.now() + hours * 3600_000).toISOString() });
              }}>
                <option value="">Snooze</option>
                <option value="1">1 hour</option>
                <option value="24">24 hours</option>
                <option value="168">1 week</option>
              </select>
            )}
          </div>
        }
      />

      <div className="ins-column">
        <Card title={<>Why <span className="badge ai">AI</span></>} className="ins-ai" actions={
          <div className="actions-row" style={{ alignItems: "center", gap: 8 }}>
            <AiBlockedTooltip ai={ai}>
              <button type="button" className="btn primary" onClick={() => void explain.start()} disabled={explain.busy || ai.blocked} title={ai.titleFor()} data-testid="cluster-explain">
                {explain.busy ? "Analyzing…" : explain.state.phase === "done" || c.aiExplanation ? "Re-explain" : "Explain"}
              </button>
            </AiBlockedTooltip>
            <AiBlockedTooltip ai={ai}>
              <button type="button" className="btn ghost" onClick={() => askWhy.open({ clusterId, label: c.title })} disabled={ai.blocked} title={ai.titleFor("Ask about this issue")}>Ask Why</button>
            </AiBlockedTooltip>
          </div>
        }>
          {(explain.state.phase === "starting" || explain.state.phase === "running") && <JobStatus job={explain.state.phase === "running" ? explain.state.job : undefined} polls={explain.state.phase === "running" ? explain.state.polls : undefined} />}
          {explain.state.phase === "rejected" && <Rejected job={explain.state.job} orgId={auth.currentOrgId} />}
          {explain.state.phase === "failed" && <div className="error-box" role="alert">{explain.state.error}</div>}
          {explain.state.phase === "done" ? (
            <ClusterExplanationView a={explain.state.artifact} sessionHref={(id) => evidenceHref(id)} />
          ) : c.aiExplanation ? (
            <AiOutput>
              <p style={{ margin: 0 }}>{c.aiExplanation.text}</p>
              {citationSessions(c.aiExplanation.citations).length > 0 && (
                <div className="cite" aria-label="Evidence">
                  <span className="small muted" style={{ marginRight: 2 }}>Based on</span>
                  {citationSessions(c.aiExplanation.citations).map((s) => {
                    const h = evidenceHref(s.sessionId);
                    return (
                      <Link key={s.sessionId} to={h.to as never} params={h.params as never} search={h.search as never} title={`${h.title} this explanation cites (${s.steps} ${s.steps === 1 ? "moment" : "moments"})`}>
                        {h.label}
                      </Link>
                    );
                  })}
                </div>
              )}
              <p className="muted small" style={{ marginTop: 8 }}>Generated {formatRelative(c.aiExplanation.generatedAt)}</p>
            </AiOutput>
          ) : explain.state.phase === "idle" ? (
            <p className="muted" style={{ margin: 0 }} data-testid="ai-placeholder">
              No explanation yet.
            </p>
          ) : null}
          <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
            <div className="actions-row" style={{ alignItems: "center", gap: 8 }}>
              {settings.isPending ? (
                <span className="skeleton" style={{ display: "inline-block", width: 260, height: 14 }} aria-busy="true" />
              ) : github?.connected && repos.length > 0 ? (
                <>
                  <select className="input" aria-label="Repository" value={chosenRepo} onChange={(e) => setRepo(e.target.value)} style={{ width: 220 }} autoComplete="off">
                    {repos.map((name) => <option key={name} value={name}>{name}</option>)}
                  </select>
                  <AiBlockedTooltip ai={ai}>
                    <button type="button" className="btn" onClick={() => void draft.start()} disabled={draft.busy || ai.blocked || !chosenRepo} title={ai.titleFor()} data-testid="draft-pr">
                      {draft.busy ? "Drafting…" : "Draft PR"}
                    </button>
                  </AiBlockedTooltip>
                </>
              ) : github?.connected ? (
                <span className="small muted" data-testid="github-hint">
                  Choose repositories before drafting a pull request. {auth.currentOrgId ? <Link to="/orgs/$org/ai" params={{ org: auth.currentOrgId }}>Open AI settings</Link> : null}
                </span>
              ) : (
                <span className="small muted" data-testid="github-hint">
                  Draft PR needs GitHub. {github?.connectUrl ? <a href={github.connectUrl}>Connect GitHub</a> : auth.currentOrgId ? <Link to="/orgs/$org/ai" params={{ org: auth.currentOrgId }}>Open AI settings</Link> : null}
                </span>
              )}
            </div>
            {(draft.state.phase === "starting" || draft.state.phase === "running") && <JobStatus job={draft.state.phase === "running" ? draft.state.job : undefined} />}
            {draft.state.phase === "rejected" && <Rejected job={draft.state.job} orgId={auth.currentOrgId} />}
            {draft.state.phase === "failed" && <div className="error-box" role="alert">{draft.state.error}</div>}
            {draft.state.phase === "done" && <PrDraftView a={draft.state.artifact} />}
          </div>
        </Card>
        <Card title="Impact">
          <dl className="ins-kv ins-kv-wide">
            <dt>Occurrences</dt><dd className="mono">{c.count.toLocaleString()} total · <span style={{ whiteSpace: "nowrap" }}>{c.count24h} last 24h</span> · <span style={{ whiteSpace: "nowrap" }}>{c.count7d} last 7d</span></dd>
            {backend ? (
              <><dt>Linked replays</dt><dd className="mono" title="Replays of the people whose requests hit this">{c.affectedSessions.toLocaleString()}{c.affectedUsers > 0 ? ` · ${c.affectedUsers.toLocaleString()} ${c.affectedUsers === 1 ? "user" : "users"}` : ""}</dd></>
            ) : (
              <><dt>Affected</dt><dd className="mono" title="Everyone this issue has hit">{audienceLabel(c.affectedUsers, c.affectedSessions)}</dd></>
            )}
            <dt>Seen</dt><dd>{formatDateTime(c.firstSeenAt)} → {formatDateTime(c.lastSeenAt)}</dd>
            <dt>Releases</dt><dd className="small">{c.releasesSeen.join(", ") || "—"}</dd>
            {c.firstRelease && (c.releasesSeen.length > 1 || !c.releasesSeen.includes(c.firstRelease)) ? (
              <><dt>Introduced</dt><dd className="small">{c.firstRelease}</dd></>
            ) : null}
            {c.errorMessage && (<><dt>Error</dt><dd className="mono small">{c.errorMessage}</dd></>)}
            {c.error && (<><dt>Fingerprint</dt><dd className="mono small">{c.error.fingerprint}</dd></>)}
            {c.assignee && (<><dt>Assignee</dt><dd title={c.assignee}>{members.data?.find((m) => m.email === c.assignee)?.name || c.assignee}</dd></>)}
            {c.snoozedUntil && (<><dt>Snoozed until</dt><dd>{formatDateTime(c.snoozedUntil)}</dd></>)}
            {c.externalLinks?.length ? (
              <><dt>Linked</dt><dd>{c.externalLinks.map((l) => (<a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="badge neutral" style={{ marginRight: 4 }}>{l.kind}{l.key ? ` ${l.key}` : ""}</a>))}</dd></>
            ) : null}
          </dl>
        </Card>
      </div>

      {c.error && (
        <Card title={c.error.symbolicated?.length ? "Stack trace (symbolicated)" : "Stack trace"}>
          {c.error.symbolicated?.length ? (
            <FrameList frames={c.error.symbolicated} />
          ) : c.error.stack ? (
            <pre className="ins-stack">{c.error.stack}</pre>
          ) : (
            <p className="muted">No stack captured. Upload source maps for this release to see original file names and line numbers.</p>
          )}
        </Card>
      )}

      <Card title={<>Occurrences per hour <span className="muted">· last 48 h</span></>}>
        <LineChart ariaLabel={`Hourly occurrences of ${c.title} over the last 48 hours`} series={[{ name: "Occurrences", points: trendSeries(c.trend) }]} area height={160} />
      </Card>

      <div className={backend ? "ins-grid-2" : "ins-grid-3"} style={{ marginTop: 16 }}>
        <Card title="By release"><BarChart ariaLabel="Occurrences by release" items={c.byRelease.map((b) => ({ label: b.release, value: b.count }))} /></Card>
        {!backend && <Card title="By browser"><BarChart ariaLabel="Occurrences by browser" items={c.byBrowser.map((b) => ({ label: b.browser, value: b.count }))} /></Card>}
        <Card title="By route"><BarChart ariaLabel="Occurrences by route" items={c.byRoute.map((b) => ({ label: b.route, value: b.count }))} /></Card>
      </div>

      {backend ? (
        <BackendOccurrences samples={samples} hasMore={!!occ.hasNextPage} loading={occ.isFetchingNextPage} loadMore={() => void occ.fetchNextPage()} />
      ) : (
      <Card title="Sample sessions">
        <table className="table ins-samples" aria-label="Sample occurrences">
          <thead><tr><th>When</th><th>Session</th><th>User</th><th>Browser</th><th>Release</th><th>Moment</th><th></th></tr></thead>
          <tbody>
            {samples.map((o) => (
              <tr key={`${o.sessionId}-${o.issue.id}`} className="static" data-testid="occurrence-row">
                <td className="small muted" title={formatDateTime(new Date(o.issue.at || o.startedAt).toISOString())}>{formatRelative(new Date(o.issue.at || o.startedAt).toISOString())}</td>
                <td className="mono small">{o.sessionId.slice(0, 12)}…</td>
                <td className="small"><EndUser id={o.endUserId} name={o.endUserName} /></td>
                <td className="small">{o.browser ?? "—"}{o.device ? ` · ${o.device}` : ""}</td>
                <td className="small muted">{o.release ?? "—"}</td>
                <td className="mono small">{formatDuration(o.issue.tStart)}</td>
                <td><Link {...replayHref(o.sessionId, o.issue.tStart)} className="btn ghost">Watch replay</Link></td>
              </tr>
            ))}
            {samples.length === 0 && (<tr className="static"><td colSpan={7} className="muted" style={{ textAlign: "center", padding: 16 }}>No sample sessions recorded yet.</td></tr>)}
          </tbody>
        </table>
        <AutoLoadMore hasMore={!!occ.hasNextPage} loading={occ.isFetchingNextPage} loadMore={() => void occ.fetchNextPage()} label="Loading more sample sessions" />
      </Card>
      )}

      <AskWhyDrawer />
    </>
  );
}
